'use client';

import { useEffect, useState } from 'react';

type Direction = 'ltr' | 'rtl';

const readDirection = (): Direction => {
    if (typeof document === 'undefined') return 'ltr';
    const body = document.body;
    if (!body) return 'ltr';
    return window.getComputedStyle(body).direction === 'rtl' ? 'rtl' : 'ltr';
};

/**
 * Page direction, kept in sync rather than sampled once.
 *
 * Tabs feeds this to Radix, which writes it onto the tab root as a `dir` attribute — so it
 * decides the subtree's real `direction`, and with it the order of the triggers and every
 * logical property under them. Reading it a single time on mount made that a race: if the app
 * sets the page direction after this effect runs, the tabs stay stuck at `ltr` on an Arabic
 * page. Firefox lost that race where Chrome won it, which is why the switcher bar only broke
 * in one of them.
 *
 * Observing both the documentElement and the body covers whichever the app happens to set,
 * and covers a change of language without a reload.
 */
export function useDirection(): Direction {
    const [direction, setDirection] = useState<Direction>('ltr');

    useEffect(() => {
        const sync = () => setDirection(readDirection());
        sync();

        const observer = new MutationObserver(sync);
        const options: MutationObserverInit = { attributes: true, attributeFilter: ['dir', 'lang', 'style', 'class'] };
        observer.observe(document.documentElement, options);
        if (document.body) observer.observe(document.body, options);

        return () => observer.disconnect();
    }, []);

    return direction;
}
